// Helpers for the public "Find me" search on the embed event log.
//
// The search runs entirely on data the embed has already loaded: the event
// document (team rosters, BSA numbers, round schedule) and the leaderboard
// (where each team currently is). Nothing here fetches or exposes anything new.

import { GameStatus, getGameStatus } from '../constants/gameConstants';
import { findRoundScheduleEntry, formatScheduleDateTime } from './roundSchedule';

const ACTIVE_STATUS = 'Active';
const STATUS_VALUES = [
  GameStatus.NOT_STARTED,
  GameStatus.IN_PROGRESS,
  GameStatus.COMPLETE,
  GameStatus.PLAYOFF
];

export const FIND_ME_RESULT_LIMIT = 20;

// Lowercase, strip accents, drop apostrophes/hyphens and collapse anything
// else that is not a letter or digit, so "O'Brien", "obrien" and "O BRIEN"
// all meet in the middle.
export const normalizeFindMeText = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’.-]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

export const normalizeBsa = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^0-9a-z]/g, '')
    .replace(/^bsa(?=\d)/, '');

// Members normally have an id; fall back to name for legacy roster entries so
// the same player selected twice still resolves to the same entry.
export const findMeMemberKey = (member) =>
  String(member?.id ?? '').trim() ||
  `${normalizeFindMeText(member?.name)}|${normalizeFindMeText(member?.surname)}`;

const isBsaMatch = (query, candidate) => {
  if (!query || !candidate) {
    return false;
  }

  if (query === candidate) {
    return true;
  }

  // A BSA number may be stored as a number or as a string, and a leading zero
  // can survive in one but not the other.
  return (
    /^\d+$/.test(query) &&
    /^\d+$/.test(candidate) &&
    Number(query) === Number(candidate)
  );
};

const getEventVenue = (event) => {
  // The event document carries the venue document it was created with; older
  // embed reads may only have the id, which must not be shown to a player.
  const name = event?.venue?.name;
  return typeof name === 'string' ? name.trim() : '';
};

const getStatus = (value) => {
  const status = Number(value);
  return STATUS_VALUES.includes(status) ? status : null;
};

const getCurrentRound = (value) => {
  const round = Number(value);
  return Number.isInteger(round) && round > 0 ? round : null;
};

const buildEntry = ({ member, team, section, stats, event, inLog }) => {
  const name = String(member?.name ?? '').trim();
  const surname = String(member?.surname ?? '').trim();

  if (!name && !surname) {
    return null;
  }

  const teamId = team?.id || stats?.teamId;
  if (!teamId) {
    return null;
  }

  const sectionId = section?.sectionId || section?.id || null;
  const currentStatus = getStatus(stats?.currentStatus);
  const currentRound = getCurrentRound(stats?.currentRound);
  const nextGameEntry =
    currentStatus && currentStatus !== GameStatus.COMPLETE && currentRound
      ? findRoundScheduleEntry(event?.roundSchedule, currentRound)
      : null;

  const teamStatus = String(team?.status || ACTIVE_STATUS);
  const playerStatus = String(member?.status || ACTIVE_STATUS);
  // A withdrawn team or player still appears in the roster, so keep them
  // searchable and let the banner explain why their game data may look stale.
  const inactiveStatus =
    [playerStatus, teamStatus].find(
      (status) => status && status !== ACTIVE_STATUS
    ) || ACTIVE_STATUS;

  return {
    key: `${sectionId || ''}|${teamId}|${findMeMemberKey(member)}`,
    sectionId,
    sectionName: String(section?.sectionName || '').trim(),
    venue: String(section?.venue || getEventVenue(event) || '').trim(),
    teamId,
    teamName: String(team?.name || stats?.teamName || '').trim(),
    teamStatus,
    playerKey: findMeMemberKey(member),
    playerName: [name, surname].filter(Boolean).join(' '),
    playerStatus: inactiveStatus,
    rink: String(stats?.rink || '').trim(),
    currentRound,
    currentStatus,
    statusLabel: currentStatus ? getGameStatus(currentStatus) : '',
    nextGame: nextGameEntry ? formatScheduleDateTime(nextGameEntry) : '',
    inLog: Boolean(inLog),
    searchName: normalizeFindMeText(`${name} ${surname}`),
    searchBsa: normalizeBsa(member?.bsaNumber),
    sortName: normalizeFindMeText(`${surname} ${name}`)
  };
};

export const buildFindMeEntries = ({
  event,
  sectionsLeaderboard = [],
  leaderboard = []
}) => {
  const teams = Array.isArray(event?.teams) ? event.teams : [];
  const teamById = new Map(
    teams.filter((team) => team?.id).map((team) => [team.id, team])
  );
  const entries = [];
  const seen = new Set();

  const add = (entry) => {
    if (!entry || seen.has(entry.key)) {
      return;
    }
    seen.add(entry.key);
    entries.push(entry);
  };

  if (event?.hasSections) {
    (Array.isArray(sectionsLeaderboard) ? sectionsLeaderboard : []).forEach(
      (section) => {
        const statsTeams = Array.isArray(section?.sectionTotals?.teams)
          ? section.sectionTotals.teams
          : [];

        statsTeams.forEach((stats) => {
          // The card does not render DNQ teams, so there is nowhere to scroll.
          if (stats?.dnq) {
            return;
          }

          const team =
            teamById.get(stats.teamId) || {
              id: stats.teamId,
              name: stats.teamName,
              status: ACTIVE_STATUS
            };
          // Identity (including the BSA number) comes from the event roster;
          // the leaderboard copy is the fallback if the roster row is missing.
          const members =
            Array.isArray(team.members) && team.members.length > 0
              ? team.members
              : Array.isArray(stats.teamPlayers)
                ? stats.teamPlayers
                : [];

          members.forEach((member) =>
            add(buildEntry({ member, team, section, stats, event, inLog: true }))
          );
        });
      }
    );

    return entries;
  }

  // Non-sectioned logs aggregate by team. Only teams that made it into the
  // totals table can be scrolled to, so mark the rest as not listed in the log.
  const loggedTeamIds = new Set(
    (Array.isArray(leaderboard) ? leaderboard : []).flatMap((group) =>
      (Array.isArray(group?.totals?.teams) ? group.totals.teams : []).map(
        (row) => row?.teamId
      )
    )
  );

  teams.forEach((team) => {
    (Array.isArray(team?.members) ? team.members : []).forEach((member) =>
      add(
        buildEntry({
          member,
          team,
          section: null,
          stats: null,
          event,
          inLog: loggedTeamIds.has(team?.id)
        })
      )
    );
  });

  return entries;
};

export const filterFindMeEntries = (
  entries,
  rawQuery,
  { limit = FIND_ME_RESULT_LIMIT } = {}
) => {
  const nameQuery = normalizeFindMeText(rawQuery);
  const bsaQuery = normalizeBsa(rawQuery);
  const tokens = nameQuery ? nameQuery.split(' ').filter(Boolean) : [];

  if (!tokens.length && !bsaQuery) {
    return { results: [], total: 0, truncated: false };
  }

  const matches = (Array.isArray(entries) ? entries : []).filter((entry) => {
    if (bsaQuery && isBsaMatch(bsaQuery, entry.searchBsa)) {
      return true;
    }

    // Every token must appear somewhere in "name surname", so a middle name,
    // surname-first search, or "j smi" all work. A partial BSA is deliberately
    // not a match: a short number would return most of the event.
    return tokens.length > 0 && tokens.every((token) => entry.searchName.includes(token));
  });

  matches.sort((a, b) => {
    const byName = (a.sortName || '').localeCompare(b.sortName || '');
    if (byName !== 0) {
      return byName;
    }

    const bySection = (a.sectionName || '').localeCompare(b.sectionName || '');
    if (bySection !== 0) {
      return bySection;
    }

    return (a.teamName || '').localeCompare(b.teamName || '');
  });

  return {
    results: matches.slice(0, limit),
    total: matches.length,
    truncated: matches.length > limit
  };
};
